import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDashboardPageState } from '../../../../hooks/useDashboardPageState';
import { useUserBlocks } from '../../../../api/healthCoach/useUserBlocks';
import { useDashboardBlocks } from '../../../../api/healthCoach/useDashboardBlocks';
import { useFetchFoodChallenges } from '../../../../api/healthCoach/useFetchFoodChallenges';
import { useAddUserBlock } from '../../../../api/healthCoach/mutations/useAddUserBlock';
import { useElimination } from '../../../../api/healthCoach/useElimination';
import { useSuspectIdentification } from '../../../../api/healthCoach/useSuspectIdentification';
import {
  ADJUSTABLE_NUTRITION_BLOCKS,
  BLOCK_STABLE_STRINGS,
  BLOCK_TYPE,
  BlockType,
  NutritionBlockType,
} from '../../../../models/block';
import type {
  BlockStableStrings,
  DashboardBlock,
  UserBlock,
} from '../../../../models/block';
import type { AnyNutritionBlockData } from '../../../../models/nutritionBlockData';
import {
  isBlockActive,
  getBlockId,
  isAssignableBlock,
  normalizeText,
} from '../utils';
import { useUserBlockAction } from '../../../../api/healthCoach/mutations/useUserBlockAction';
import { BlockDrawerMode, FilterOption, UserBlockAction } from '../types';
import useUpdateUserBlock from '../../../../api/healthCoach/mutations/useUpdateUserBlock';
import { archivedFilterOptions } from '../constants';

type NutritionBlockDetails = {
  data: AnyNutritionBlockData | null;
  nutritionBlockType: BlockStableStrings | null;
};

const laneLabelByType: Partial<Record<BlockType, string>> = {
  [BLOCK_TYPE.Clinical]: 'Clinical',
  [BLOCK_TYPE.Nutrition]: 'Nutrition',
  [BLOCK_TYPE.MindGut]: 'Mind-Gut',
  [BLOCK_TYPE.BehaviorChange]: 'Behavior Change',
};

export const useOverviewPage = () => {
  const { patient = '' } = useParams();
  const {
    state: { user },
  } = useDashboardPageState();
  const [archivedFilter, setArchivedFilter] = useState<FilterOption>(
    archivedFilterOptions[0]
  );
  const {
    blocks: userBlocks,
    isLoading,
    isError,
  } = useUserBlocks({ archived: archivedFilter.value });
  const { mutation: addUserBlock } = useAddUserBlock();
  const { mutation: userBlockAction } = useUserBlockAction();
  const { mutation: updateUserBlockMutation } = useUpdateUserBlock();

  const [isBlockDrawerOpen, setIsBlockDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<BlockDrawerMode>(
    BlockDrawerMode.Create
  );
  const [selectedLaneBlock, setSelectedLaneBlock] = useState<UserBlock | null>(
    null
  );
  const [selectedCreateBlock, setSelectedCreateBlock] =
    useState<DashboardBlock | null>(null);
  const [selectedCreateBlockType, setSelectedCreateBlockType] =
    useState<BlockType>(BLOCK_TYPE.Nutrition);
  const [patientInstructions, setPatientInstructions] = useState('');
  const resetCreateBlockMutationState = () => {
    addUserBlock.reset();
  };

  const {
    blocks: dashboardBlocks,
    isLoading: isLoadingDashboardBlocks,
    isError: isDashboardBlocksError,
  } = useDashboardBlocks({
    blockType: selectedCreateBlockType,
    enabled: isBlockDrawerOpen && drawerMode === BlockDrawerMode.Create,
  });

  const {
    suspectIdentification,
    isLoading: isLoadingSuspectIdentification,
    isError: isSuspectIdentificationError,
  } = useSuspectIdentification({
    enabled:
      isBlockDrawerOpen &&
      drawerMode === BlockDrawerMode.Details &&
      selectedLaneBlock?.stable_string ===
        BLOCK_STABLE_STRINGS.identifySuspects &&
      !!selectedLaneBlock?.id,
    suspectIdentificationOrUserBlockId: selectedLaneBlock?.id ?? '',
    userId: patient,
  });

  const {
    elimination,
    isLoading: isLoadingElimination,
    isError: isEliminationError,
  } = useElimination({
    enabled:
      isBlockDrawerOpen &&
      drawerMode === BlockDrawerMode.Details &&
      selectedLaneBlock?.stable_string ===
        BLOCK_STABLE_STRINGS.eliminationChallenge &&
      !!selectedLaneBlock?.id,
    eliminationOrUserBlockId: selectedLaneBlock?.id ?? '',
    userId: patient,
  });

  const {
    activeFoodChallenges,
    isLoading: isLoadingFoodChallenges,
    isError: isFoodChallengesError,
  } = useFetchFoodChallenges({
    enabled:
      isBlockDrawerOpen &&
      drawerMode === BlockDrawerMode.Details &&
      selectedLaneBlock?.stable_string === BLOCK_STABLE_STRINGS.foodChallenge &&
      !!selectedLaneBlock?.id,
    userBlockId: selectedLaneBlock?.id ?? '',
  });

  const lanes = useMemo<Partial<Record<BlockType, UserBlock[]>>>(() => {
    return (userBlocks ?? []).reduce<Partial<Record<BlockType, UserBlock[]>>>(
      (acc, block: UserBlock) => {
        const laneKey = block.block_type;
        if (!laneKey || acc[laneKey] == null) {
          return acc;
        }

        acc[laneKey]!.push(block);

        return acc;
      },
      {
        [BLOCK_TYPE.Clinical]: [],
        [BLOCK_TYPE.Nutrition]: [],
        [BLOCK_TYPE.MindGut]: [],
        [BLOCK_TYPE.BehaviorChange]: [],
      }
    );
  }, [userBlocks]);

  const blockOptions = useMemo<DashboardBlock[]>(() => {
    return dashboardBlocks.filter(
      (block) => block.block_type === selectedCreateBlockType
    );
  }, [dashboardBlocks, selectedCreateBlockType]);

  // Names of blocks the user is currently actively assigned in the lane the
  // CTM is about to add to — used to disable duplicates in the create list.
  const activeAssignedBlockNames = useMemo<Set<string>>(() => {
    return new Set(
      userBlocks
        .filter(
          (block) =>
            block.block_type === selectedCreateBlockType && isBlockActive(block)
        )
        .map((block) => normalizeText(block.block_name))
    );
  }, [userBlocks, selectedCreateBlockType]);

  const onCreateBlock = async (block: DashboardBlock) => {
    if (!patient || !isAssignableBlock(block)) return;

    const blockId = getBlockId(block);
    if (!blockId) return;

    resetCreateBlockMutationState();

    await addUserBlock.mutateAsync({
      blockId,
      instructions: patientInstructions,
      userId: patient,
    });

    setIsBlockDrawerOpen(false);
    setSelectedCreateBlock(null);
    setSelectedLaneBlock(null);
    setSelectedCreateBlockType(BLOCK_TYPE.Nutrition);
    setDrawerMode(BlockDrawerMode.Create);
    setPatientInstructions('');
  };

  const handleUserBlockAction = async (
    action: UserBlockAction,
    userBlockId?: string
  ) => {
    if (userBlockId) {
      await userBlockAction.mutateAsync({
        userId: patient,
        userBlockId,
        action,
      });
    }

    closeBlockDrawer();
  };

  const closeBlockDrawer = () => {
    resetCreateBlockMutationState();
    setIsBlockDrawerOpen(false);
    setSelectedCreateBlock(null);
    setSelectedLaneBlock(null);
    setSelectedCreateBlockType(BLOCK_TYPE.Nutrition);
    setDrawerMode(BlockDrawerMode.Create);
    setPatientInstructions('');
  };

  const handleUpdateUserBlock = async () => {
    const userBlockId = selectedLaneBlock?.id;

    if (userBlockId) {
      await updateUserBlockMutation.mutateAsync({
        instructions: patientInstructions,
        userBlockId,
        userId: patient,
      });

      closeBlockDrawer();
    }
  };

  const openCreateBlockDrawer = (
    blockType: BlockType = BLOCK_TYPE.Nutrition
  ) => {
    resetCreateBlockMutationState();
    setDrawerMode(BlockDrawerMode.Create);
    setSelectedLaneBlock(null);
    setSelectedCreateBlock(null);
    setSelectedCreateBlockType(blockType);
    setPatientInstructions('');
    setIsBlockDrawerOpen(true);
  };

  const openEditBlockDrawer = (block: UserBlock) => {
    resetCreateBlockMutationState();
    setDrawerMode(BlockDrawerMode.Edit);
    setSelectedLaneBlock(block);
    setSelectedCreateBlock(null);
    setPatientInstructions(block.instructions || '');
    setIsBlockDrawerOpen(true);
  };

  const openNutritionBlockDetailsDrawer = (block: UserBlock) => {
    if (
      !block.stable_string ||
      !ADJUSTABLE_NUTRITION_BLOCKS.includes(block.stable_string) ||
      !block.id
    ) {
      return;
    }

    resetCreateBlockMutationState();
    setDrawerMode(BlockDrawerMode.Details);
    setSelectedLaneBlock(block);
    setSelectedCreateBlock(null);
    setPatientInstructions('');
    setIsBlockDrawerOpen(true);
  };

  const onBackToBlockList = () => {
    resetCreateBlockMutationState();
    setSelectedCreateBlock(null);
  };

  const onSelectCreateBlock = (block: DashboardBlock) => {
    resetCreateBlockMutationState();
    setSelectedCreateBlock(block);
  };

  const drawerHeader =
    drawerMode === BlockDrawerMode.Details
      ? selectedLaneBlock?.block_name ?? ''
      : drawerMode === BlockDrawerMode.Edit
      ? `Edit ${
          laneLabelByType[
            selectedLaneBlock?.block_type ?? selectedCreateBlockType
          ] ?? ''
        } Block`
      : `Create ${laneLabelByType[selectedCreateBlockType] ?? ''} Block`;

  const drawerSubtitle =
    drawerMode === BlockDrawerMode.Details
      ? 'Block assignment details'
      : `Customize this care block for ${
          user?.first_name ? `${user.first_name}'s` : "the member's"
        } treatment plan.`;

  const isCreateMode = drawerMode === BlockDrawerMode.Create;
  const isDetailsMode = drawerMode === BlockDrawerMode.Details;
  const isSubmittingCreateBlock = isCreateMode && !!selectedCreateBlock;
  const detailsNutritionBlockType = selectedLaneBlock?.stable_string ?? null;

  const nutritionBlockDetailsData: AnyNutritionBlockData | null =
    detailsNutritionBlockType === NutritionBlockType.IdentifySuspectTriggerFoods
      ? suspectIdentification
      : detailsNutritionBlockType === NutritionBlockType.EliminationChallenge
      ? elimination
      : detailsNutritionBlockType === NutritionBlockType.FoodChallenge
      ? activeFoodChallenges
      : null;

  const nutritionBlockDetails: NutritionBlockDetails = {
    data: nutritionBlockDetailsData,
    nutritionBlockType: detailsNutritionBlockType,
  };

  const isDetailsLoading =
    detailsNutritionBlockType === NutritionBlockType.IdentifySuspectTriggerFoods
      ? isLoadingSuspectIdentification
      : detailsNutritionBlockType === NutritionBlockType.EliminationChallenge
      ? isLoadingElimination
      : detailsNutritionBlockType === NutritionBlockType.FoodChallenge
      ? isLoadingFoodChallenges
      : false;

  const isDetailsError =
    detailsNutritionBlockType === NutritionBlockType.IdentifySuspectTriggerFoods
      ? isSuspectIdentificationError
      : detailsNutritionBlockType === NutritionBlockType.EliminationChallenge
      ? isEliminationError
      : detailsNutritionBlockType === NutritionBlockType.FoodChallenge
      ? isFoodChallengesError
      : false;

  const isEditMode = drawerMode === BlockDrawerMode.Edit;

  const isDrawerLoading = isDetailsMode
    ? isDetailsLoading
    : isEditMode
    ? userBlockAction.isLoading || updateUserBlockMutation.isLoading
    : isSubmittingCreateBlock
    ? addUserBlock.isLoading
    : isLoadingDashboardBlocks;

  const drawerError = () => {
    if (isDetailsMode && isDetailsError) {
      return "We couldn't load this block check-in. Please try again later.";
    } else if (isSubmittingCreateBlock && addUserBlock.isError) {
      return "We couldn't create this block. Please try again later.";
    } else if (
      !isDetailsMode &&
      !isEditMode &&
      !isSubmittingCreateBlock &&
      isDashboardBlocksError
    ) {
      return `We couldn't load ${
        laneLabelByType[selectedCreateBlockType]?.toLowerCase() ?? 'care'
      } block types. Please try again later.`;
    } else if (isEditMode) {
      if (userBlockAction.isError) {
        return `We couldn't perform the selected action on the ${selectedLaneBlock?.block_name} block`;
      } else if (updateUserBlockMutation.isError) {
        return `We couldn't update the ${selectedLaneBlock?.block_name} block`;
      }
    }
  };

  return {
    activeAssignedBlockNames,
    drawerHeader,
    drawerMode,
    drawerSubtitle,
    drawerError: drawerError(),
    isDrawerLoading,
    isLoading,
    isBlockDrawerOpen,
    isUserBlocksError: isError,
    laneLabelByType,
    lanes,
    blockOptions,
    closeBlockDrawer,
    handleUpdateUserBlock,
    handleUserBlockAction,
    onBackToBlockList,
    onCreateBlock,
    onPatientInstructionsChange: setPatientInstructions,
    onSelectBlock: onSelectCreateBlock,
    openCreateBlockDrawer,
    openEditBlockDrawer,
    openNutritionBlockDetailsDrawer,
    patientInstructions,
    selectedLaneBlock,
    nutritionBlockDetails,
    selectedBlock: selectedCreateBlock,
    archivedFilter,
    setArchivedFilter,
  };
};
